/** @forge-header
 * @id          ufcParser
 * @role        UFC ticker parser | KXUFCFIGHT fight parsing
 * @pattern     pure-fn
 * @purity      none
 * @units       n/a
 * @upstream    kalshiTicker/types:ParsedTicker,TennisMatchInfo | kalshiTicker/helpers:parseFallback,formatTennisPlayerCode
 * @downstream  kalshiTicker/parse
 * @exports     parseUfcFight:fn
 * @test        parse.test.ts (indirect)
 */

import type { ParsedTicker, TennisMatchInfo } from '../types';
import { parseDateFromTicker } from '../types';
import { parseFallback, formatTennisPlayerCode, splitCompetitors } from '../helpers';

/**
 * UFC fight: KXUFCFIGHT-26JUL11MCGHOL-MCG
 * Structurally identical to a tennis match — competitor codes concatenated
 * after the YYMONDD date, one ticker per fighter outcome. Reuses the
 * two-competitor `TennisMatchInfo` shape via the `fight` field.
 *
 * The pack is split on the market ticker's OUTCOME suffix, which names one
 * fighter's exact code — same rule the boxing parser uses, and the only one
 * that survives an uneven pack. Midpoint is the fallback for outcome-less
 * (event) tickers.
 *
 * These codes are ticker fragments and stay abbreviated; the readable fighter
 * names come from the live frame (`subtitle` / hydra's canonical fields), not
 * from here.
 */
export function parseUfcFight(raw: string): ParsedTicker {
  const parts = raw.split('-');
  if (parts.length < 2) {
    return parseFallback(raw);
  }

  const mid = parts[1] ?? '';
  if (mid.length < 13) {
    return parseFallback(raw);
  }

  const date = parseDateFromTicker(mid.slice(0, 7));
  const fightersStr = mid.slice(7);
  const outcome = parts[2] ?? null;
  const [c1, c2] = splitCompetitors(fightersStr, outcome);

  const fight: TennisMatchInfo = {
    player1: { playerCode: c1, displayName: formatTennisPlayerCode(c1) },
    player2: { playerCode: c2, displayName: formatTennisPlayerCode(c2) },
  };

  const shortLabel = outcome
    ? `UFC: ${formatTennisPlayerCode(outcome)} Win`
    : 'UFC Fight';

  const fullLabel = `UFC: ${fight.player1.displayName} vs ${fight.player2.displayName}${outcome ? ` · ${formatTennisPlayerCode(outcome)} Win` : ''}`;

  return {
    raw,
    category: 'ufc-fight',
    series: 'KXUFCFIGHT',
    date,
    game: null,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    fight,
    outcome,
    shortLabel,
    fullLabel,
  };
}
